import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkUser, getUserInfo, saveUserInfo, isLoggedIn } from '../services/authService';
import TaxiLoader from '../components/TaxiLoader';
import '../styles/Login.css';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [tcKimlik, setTcKimlik] = useState('');
  const [telefon, setTelefon] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState<string>('');

  // Sayfa yüklendiğinde login durumunu kontrol et ve geçici token'ları temizle
  useEffect(() => {
    // Geçici token'ları temizle
    localStorage.removeItem('temp_token');
    sessionStorage.removeItem('temp_token');
    localStorage.removeItem('payment_token');
    sessionStorage.removeItem('payment_token');
    
    if (isLoggedIn()) {
      navigate('/');
    }
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setDebugInfo('Giriş deneniyor...');

    // TC kimlik ve telefon numarası kontrolü
    if (tcKimlik.length !== 11) {
      setError('TC Kimlik numarası 11 haneli olmalıdır.');
      setLoading(false);
      return;
    }

    if (telefon.length !== 10) {
      setError('Telefon numarası 10 haneli olmalıdır.');
      setLoading(false);
      return;
    }

    try {
      // Gerçek API çağrısı
      setDebugInfo('API isteği yapılıyor: ' + tcKimlik + ', ' + telefon);
      const response = await checkUser(tcKimlik, telefon);
      console.log('API yanıtı:', response);
      setDebugInfo('API yanıtı: ' + JSON.stringify(response));
      
      if (response.success) {
        // SMS doğrulama için sadece token'ı geçici olarak sakla
        if (response.token) {
          localStorage.setItem('temp_token', response.token);
          sessionStorage.setItem('temp_token', response.token);
        }
        
        // SMS doğrulama sayfasına yönlendir - henüz kullanıcı bilgilerini kaydetme
        navigate('/sms-verification', { 
          state: { 
            phoneNumber: telefon, 
            token: response.token || '',
            kimlikno: tcKimlik,
            rememberMe: rememberMe
          } 
        });
      } else {
        setError(response.message || 'Kullanıcı bilgileri doğrulanamadı.');
      }
    } catch (err: any) {
      console.error('Login hatası:', err);
      setDebugInfo('Hata detayı: ' + (err.message || JSON.stringify(err)));
      setError('Giriş işlemi sırasında bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  // Telefon numarasını formatlama fonksiyonu
  const formatTelefon = (telefon: string): string => {
    const cleaned = telefon.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    
    if (match) {
      return match[1] + ' ' + match[2] + ' ' + match[3];
    }
    
    return telefon;
  };

  return (
    <div className="login-container">
      {loading && <TaxiLoader message="Giriş yapılıyor..." />}
      <div className="login-box">
        <img 
          src="/images/logo2024.png" 
          alt="İstanbul Havalimanı Taksi Kooperatifi" 
          className="login-logo"
        />
        <h2>Üye Girişi</h2>
        
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="tcKimlik">TC Kimlik No</label>
            <input
              type="text"
              id="tcKimlik"
              value={tcKimlik}
              onChange={(e) => setTcKimlik(e.target.value.replace(/[^0-9]/g, '').substring(0, 11))}
              placeholder="TC Kimlik numaranızı giriniz"
              maxLength={11}
              required
            />
            {tcKimlik.length > 0 && tcKimlik.length < 11 && (
              <small className="input-hint">TC Kimlik numarası 11 haneli olmalıdır</small>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="telefon">Telefon</label>
            <div className="phone-input-container">
              <span className="phone-prefix">+90</span>
              <input
                type="tel"
                id="telefon"
                value={telefon}
                onChange={(e) => setTelefon(e.target.value.replace(/[^0-9]/g, '').substring(0, 10))}
                placeholder="5XX XXX XXXX"
                maxLength={10}
                required
              />
            </div>
            {telefon.length > 0 && telefon.length < 10 && (
              <small className="input-hint">Telefon numarası 10 haneli olmalıdır</small>
            )}
          </div>
          
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span className="checkbox-text">Beni Hatırla (30 gün)</span>
            </label>
          </div>
          
          <button 
            type="submit" 
            className="login-button" 
            disabled={loading || tcKimlik.length !== 11 || telefon.length !== 10}
          >
            {loading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
          </button>
        </form>
        
        {error && <div className="error-message">{error}</div>}
        {debugInfo && process.env.NODE_ENV === 'development' && (
          <div className="debug-info" style={{ fontSize: '12px', marginTop: '10px', color: '#666' }}>
            <details>
              <summary>Hata Ayıklama Bilgisi</summary>
              <pre>{debugInfo}</pre>
            </details>
          </div>
        )}
        
        <div className="login-footer">
          <p>İstanbul Havalimanı Taksiciler Kooperatifi</p>
          <p>Copyright © {new Date().getFullYear()} Tüm Hakları Saklıdır</p>
        </div>
      </div>
    </div>
  );
};

export default Login; 